import PropTypes from "prop-types"
import React, { useContext, useEffect, useRef, useState } from "react"
import { withTranslation } from "react-i18next"
import {
  Card,
  CardBody,
  Col,
  CardTitle,
  Row,
  Button,
  Form,
  FormGroup,
  Label,
  Alert,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
} from "reactstrap"

import { isEqual } from "lodash"

import * as Yup from "yup"
import { useFormik } from "formik"
import {
  geocodeGetCoordinates,
  getClientData,
  updateClientInfo,
} from "../../helpers/backend_helper"
import GlobalState from "contexts/GlobalState"
import PubSub from "pubsub-js"
import Lottie from "react-lottie"
import {
  Switch,
  Space,
  Tag,
  Select,
  Input as AntdInput,
  Form as AntdForm,
  InputNumber,
} from "antd"
import { getLottieLoadingOptions, showToast } from "utils/general"
import SelectGroup from "components/DropdownList/SelectGroup"
import DefaultMap from "components/GoogleMap/map"
const { TextArea } = AntdInput

const ClientInfo = props => {
  const [state] = useContext(GlobalState)
  const [clientData, setClientData] = useState(null)
  const [editMode, setEditMode] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [userRole, setUserRole] = useState(null)
  const [loading, setLoading] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [geolocating, setGeolocating] = useState(false)
  const [contacts, setContacts] = useState([])
  const [contactsEdit, setContactsEdit] = useState([])
  const [mapModal, setMapModal] = useState(false)
  const [tagsEdit, setTagsEdit] = useState([])

  const abortRef = useRef(null)
  useEffect(() => {
    abortRef.current = new AbortController()
    const token = PubSub.subscribe("page-refresh", (event, data) => {
      getData()
    })
    if (state.authUser != null) {
      const obj = JSON.parse(state.authUser)
      let role = obj.activeRole ? obj.activeRole : obj.role
      setUserRole(role)
      if (role === "system_admin") {
        setIsAdmin(true)
      }
    }
    return () => {
      abortRef.current.abort()
      PubSub.unsubscribe(token)
    }
  }, [])

  useEffect(() => {
    if (props.suvantoKey) {
      getData()
    }
  }, [props.suvantoKey])

  useEffect(() => {
    if (props.suvantoKey) {
      setContactsEdit(contacts)
      setTagsEdit(clientData?.clientTags ?? [])
    }
  }, [editMode])

  // Get client data
  const getData = () => {
    if (props.suvantoKey) {
      setLoading(true)
      getClientData(abortRef.current, props.suvantoKey, false)
        .then(result => {
          if (result.key) {
            //console.log("CLIENT DATA: ", result)
            if (result.timezoneOffset != null) {
              if (result.timezoneOffset < 0)
                result.timezoneOffsetString = "UTC" + result.timezoneOffset
              else result.timezoneOffsetString = "UTC+" + result.timezoneOffset
            }
            setClientData(result)

            let tempContacts = []
            if (result.contacts) {
              let split = result.contacts.split(",")
              for (const element of split) {
                if (element.indexOf("+") > 0) {
                  let splat = element.split("+")
                  let temp = new Object()
                  temp.name = splat[0].trim()
                  temp.gsm = "+" + splat[1].trim()
                  tempContacts.push(temp)
                } else {
                  let temp = new Object()
                  temp.name = element.trim()
                  temp.gsm = ""
                  tempContacts.push(temp)
                }
              }
            }
            setContacts(tempContacts)
          }
          setLoading(false)
        })
        .catch(err => {
          console.log(err)
          setLoading(false)
        })
    }
  }

  // Filter unchanged values
  const filterUnchangedValues = (originalValues, newValues) => {
    const changedValues = {}

    for (const key in newValues) {
      if (!isEqual(originalValues[key], newValues[key])) {
        changedValues[key] = newValues[key]
      }
    }
    return changedValues
  }

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: clientData?.name || "",
      group: clientData?.group || "",
      gsmNumber: clientData?.gsmNumber || "",
      address: clientData?.address || "",
      ssnNumber: clientData?.ssn || "",
      timezoneOffset: clientData?.timezoneOffset || "0",
      language: clientData?.language || "fi",
      contacts: clientData?.contacts || "",
      displayName: clientData?.displayName || "",
      appOtherInfo: clientData?.appOtherInfo || "",
      information: clientData?.information || "",
      storeAlarmsPeriod: clientData?.alarmRetentionPeriod || "0",
      storeHealthDataPeriod: clientData?.healthRetentionPeriod || "0",
      storeVideoDataPeriod: clientData?.videoRetentionPeriod || "0",
      away: clientData?.away || false,
      timezoneCheckEnabled: clientData?.timezoneCheckEnabled || false,
      latitude: clientData?.coordinates?.latitude || "",
      longitude: clientData?.coordinates?.longitude || "",
      videoDisplayName: clientData?.video?.displayName || "",
      videoCallFeedback: clientData?.video?.videoCallFeedback || false,
      autoJoinGroupCall: clientData?.autoJoinGroupCall || false,
      storeTrackerDataPeriod: clientData?.tracker?.dataRetention || "0",
      storeHomeDataPeriod: clientData?.home?.dataRetention || "14",
      restStartHour: clientData?.home?.restStartHour || "20",
      learningPeriod: clientData?.home?.learningPeriod || "14",
    },
    validationSchema: Yup.object({
      displayName: Yup.string().matches(
        /^[a-zA-Z0-9\s\-()!?&.,+#äöåøæÄÖÅÆØ]*$/,
        props.t("ContainsNotAllowedCharacters")
      ),
      videoDisplayName: Yup.string().matches(
        /^[a-zA-Z0-9\s\-()!?&.,+#äöåøæÄÖÅÆØ]*$/,
        props.t("ContainsNotAllowedCharacters")
      ),
      name: Yup.string()
        .matches(
          /^[a-zA-Z0-9\s\-()!?&.,+#äöåøæÄÖÅÆØ]*$/,
          props.t("ContainsNotAllowedCharacters")
        )
        .trim()
        .min(3, props.t("Too short"))
        .required(props.t("Required field")),
      group: Yup.string().required(props.t("Required field")),
    }),

    onSubmit: values => {
      //console.log("Submit: ", values)

      const initialFormValues = structuredClone(validation.initialValues)

      // Filter out unchanged values
      const changedValues = filterUnchangedValues(initialFormValues, values)
      //console.log("Filtered changed: ", changedValues)
      let temp = { ...changedValues }

      if (temp.timezoneOffset) {
        temp.timezoneOffset = Number.parseInt(values.timezoneOffset, 10)
      }
      if (temp.storeAlarmsPeriod) {
        temp.storeAlarmsPeriod = Number.parseInt(values.storeAlarmsPeriod, 10)
      }
      if (temp.storeHealthDataPeriod) {
        temp.storeHealthDataPeriod = Number.parseInt(values.storeHealthDataPeriod, 10)
      }
      if (temp.storeVideoDataPeriod) {
        temp.storeVideoDataPeriod = Number.parseInt(values.storeVideoDataPeriod, 10)
      }
      if (temp.storeTrackerDataPeriod != null && temp.storeTrackerDataPeriod)
        temp.storeTrackerDataPeriod = Number.parseInt(
          values.storeTrackerDataPeriod,
          10
        )
      if (temp.storeHomeDataPeriod != null && temp.storeHomeDataPeriod)
        temp.storeHomeDataPeriod = Number.parseInt(values.storeHomeDataPeriod, 10)
      if (temp.learningPeriod != null && temp.learningPeriod)
        temp.learningPeriod = Number.parseInt(values.learningPeriod, 10)

      if (temp.latitude || temp.longitude) {
        let coords = new Object()
        let lat = Number.parseFloat(values.latitude)
        let lng = Number.parseFloat(values.longitude)
        coords.latitude = Number.isNaN(lat) ? 0 : lat
        coords.longitude = Number.isNaN(lng) ? 0 : lng
        coords.elevation = 0
        temp.coordinates = coords
      }

      if (contactsEdit !== contacts) {
        console.log("Contacts changed:", contactsEdit, contacts)
        // Contacts
        let contactString = ""
        for (const element of contactsEdit) {
          let con = element.name.trim() + " " + element.gsm.trim()
          if (contactString.length == 0) contactString = con
          else contactString = contactString + "," + con
        }
        temp.contacts = contactString
      }

      if (tagsEdit !== clientData.clientTags && isAdmin) {
        console.log("Tags changed:", tagsEdit, clientData.clientTags)
        temp.clientTags = tagsEdit
      }

      console.log("Final Submit: ", temp)

      if (temp.name) {
        props.setClientName(temp.name)
      }

      if (clientData?.key) {
        setUpdating(true)
        updateClientInfo(abortRef.current, clientData.key, temp)
          .then(result => {
            // console.log(result)
            setUpdating(false)
            if (result.statusCode == 200) {
              setEditMode(false)
              getData()
              showToast(props.t("Information updated"), "success")
            } else {
              showToast(props.t("Failed to update information"), "error")
            }
          })
          .catch(err => {
            console.log(err)
            setUpdating(false)
            showToast(props.t("Failed to update information"), "error")
          })
      }
    },
  })

  // New contact validation
  const contactValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      gsm: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required(props.t("Required field")),
      gsm: Yup.string().matches(
        /^(\+)[1-9]\d{7,32}$/,
        props.t("Invalid format")
      ),
      // Not required as the button is disabled if field is empty.
      // Gives unnecessary feedback if the field is cleared.
      // .required(props.t("Required field")),
    }),
    onSubmit: values => {
      console.log("Submit: ", values)

      let temp = new Object()
      temp.name = values.name
      temp.gsm = values.gsm

      setContactsEdit(prevState => [...prevState, temp])
      contactValidation.resetForm()
    },
  })

  // Client tag validation
  const tagValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      category: "",
      tag: "",
    },
    validationSchema: Yup.object({
      category: Yup.string()
        .trim()
        .min(4, props.t("Too short"))
        .matches(
          /^[a-zA-Z0-9\s\-\/():&+#äöåøæÄÖÅÆØ]*$/,
          props.t("ContainsNotAllowedCharacters")
        ),
      tag: Yup.string()
        .trim()
        .min(4, props.t("Too short"))
        .matches(
          /^[a-zA-Z0-9\s\-\/():&+#äöåøæÄÖÅÆØ]*$/,
          props.t("ContainsNotAllowedCharacters")
        ),
    }),
    onSubmit: values => {
      console.log("Submit: ", values)
      let finalTag = values.tag.trim()
      if (finalTag.length >= 4) {
        let finalCat = values.category.trim()
        if (finalCat.length >= 4) finalTag = finalCat + " > " + finalTag

        if (!tagsEdit.includes(finalTag)) {
          setTagsEdit(prevState => [...prevState, finalTag])
          tagValidation.resetForm()
        }
      }
    },
  })

  // Get coordinates
  const getCoordinates = () => {
    if (validation.values.address && !geolocating) {
      setGeolocating(true)
      geocodeGetCoordinates(abortRef.current, validation.values.address)
        .then(result => {
          //console.log("COORDS: ", result)
          if (result.statusCode == 200 && result.coordinates) {
            validation.setFieldValue("latitude", "" + result.coordinates.lat)
            validation.setFieldValue("longitude", "" + result.coordinates.lng)
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setGeolocating(false)
        })
    }
  }

  // Location changed in map
  const mapLocationChanged = latLng => {
    console.log("mapLocationChanged", latLng)
    validation.setFieldValue("latitude", latLng.lat.toFixed(6))
    validation.setFieldValue("longitude", latLng.lng.toFixed(6))
  }

  // Get translatable language
  const translateLang = lang => {
    switch (lang) {
      case "fi":
        return "Finnish"
      case "en":
        return "English"
      case "sv":
        return "Swedish"
      default:
        return "Undefined"
    }
  }

  // Group selection changed
  const groupChanged = newGroup => {
    validation.setFieldValue("group", newGroup)
  }

  return (
    <>
      {loading && (
        <Lottie options={getLottieLoadingOptions()} height={100} width={100} />
      )}

      {clientData && !loading && (
        <>
          <Card className="mt-3">
            <CardBody>
              <CardTitle className="mb-4">
                <Row>
                  <Col className="d-flex" style={{ alignItems: "center" }}>
                    <i className="bx bxs-user-circle text-truncate align-middle me-2"></i>
                    {props.t("Personal information")}
                  </Col>

                  <Col className="col-auto">
                    {!editMode &&
                      (!clientData.disableProfessionalsClientEdit ||
                        (clientData.disableProfessionalsClientEdit &&
                          userRole !== "organisation_user")) && (
                        <Button
                          color="primary"
                          size="sm"
                          className=""
                          onClick={() => {
                            setEditMode(true)
                          }}
                        >
                          <i className="me-2 bx bxs-edit text-truncate align-middle"></i>
                          <span className="">{props.t("Edit")}</span>
                        </Button>
                      )}
                    {editMode && (
                      <>
                        <Button
                          color="primary"
                          size="sm"
                          className=""
                          onClick={() => {
                            setEditMode(false)
                            validation.resetForm()
                            contactValidation.resetForm()
                          }}
                        >
                          <i className="me-2 bx bxs-x-square text-truncate align-middle"></i>
                          <span className="">{props.t("Cancel")}</span>
                        </Button>
                        <Button
                          color="success"
                          size="sm"
                          disabled={updating}
                          className="ms-3"
                          onClick={() => {
                            validation.submitForm()
                          }}
                        >
                          <i className="me-2 bx bxs-save text-truncate align-middle"></i>
                          <span className="">{props.t("Save changes")}</span>
                        </Button>
                      </>
                    )}
                  </Col>
                </Row>
              </CardTitle>

              {!editMode && (
                <>
                  <Row>
                    <Col xs={12} lg={8}>
                      <Row xs={1} sm={2} className="mt-3">
                        <Col>
                          <div className="text-muted mb-4">
                            <h6>{props.t("Name")}:</h6> {clientData.name}
                          </div>
                        </Col>
                        {userRole != "private_user" && (
                          <Col>
                            <div className="text-muted mb-4">
                              <h6>{props.t("Group")}:</h6> {clientData.group}
                            </div>
                          </Col>
                        )}
                        <Col>
                          <div className="text-muted mb-4">
                            <h6>{props.t("GSM")}:</h6>{" "}
                            {clientData.gsmNumber
                              ? "+" + clientData.gsmNumber
                              : props.t("Undefined")}
                          </div>
                        </Col>
                        <Col>
                          <div className="text-muted mb-4">
                            <h6>{props.t("SSN")}:</h6>{" "}
                            {clientData.ssn
                              ? clientData.ssn
                              : props.t("Undefined")}
                          </div>
                        </Col>
                        <Col>
                          <div className="text-muted mb-4">
                            <h6>{props.t("Address")}:</h6>{" "}
                            {clientData.address
                              ? clientData.address
                              : props.t("Undefined")}
                            {clientData.coordinates &&
                              clientData.coordinates.latitude != 0 && (
                                <div>
                                  <a
                                    target="_blank"
                                    rel="noreferrer"
                                    href={
                                      "https://www.google.com/maps/place/" +
                                      clientData.coordinates.latitude +
                                      "," +
                                      clientData.coordinates.longitude
                                    }
                                  >
                                    {clientData.coordinates?.latitude +
                                      ", " +
                                      clientData.coordinates?.longitude}
                                  </a>
                                </div>
                              )}
                          </div>
                        </Col>
                        <Col>
                          <div className="text-muted mb-4">
                            <h6>{props.t("Timezone")}:</h6>{" "}
                            {clientData.timezoneOffsetString
                              ? clientData.timezoneOffsetString
                              : props.t("Undefined")}
                            {clientData.timezoneCheckEnabled && (
                              <p>{props.t("Automatic")}</p>
                            )}
                          </div>
                        </Col>
                        <Col>
                          <div className="text-muted mb-4">
                            <h6>{props.t("Language")}:</h6>{" "}
                            {props.t(translateLang(clientData.language))}
                          </div>
                        </Col>
                        <Col>
                          <div className="text-muted mb-4">
                            <h6>{props.t("App display name")}:</h6>{" "}
                            {clientData.displayName
                              ? clientData.displayName
                              : props.t("Undefined")}
                          </div>
                        </Col>
                        {clientData?.videoCallRoomName && (
                          <Col>
                            <div className="text-muted mb-4">
                              <h6>{props.t("Video call display name")}:</h6>{" "}
                              {clientData?.video?.displayName
                                ? clientData.video.displayName
                                : props.t("Undefined")}
                            </div>
                          </Col>
                        )}
                        <Col>
                          <div className="text-muted mb-4">
                            <h6>
                              {props.t("Additional notes") +
                                ", " +
                                props.t("visible in app")}
                              :
                            </h6>{" "}
                            {clientData.appOtherInfo
                              ? clientData.appOtherInfo
                              : props.t("Undefined")}
                          </div>
                        </Col>
                        {(isAdmin || userRole === "organisation_admin") && (
                          <Col>
                            <div className="text-muted mb-4">
                              <h6>{props.t("Admin notes")}:</h6>{" "}
                              {clientData.information
                                ? clientData.information
                                : props.t("Undefined")}
                            </div>
                          </Col>
                        )}
                      </Row>
                    </Col>
                    <Col xs={12} lg={4}>
                      {clientData?.coordinates?.latitude != null &&
                      clientData?.coordinates?.latitude != 0 &&
                      clientData?.coordinates?.longitude != null &&
                      clientData?.coordinates?.longitude != 0 ? (
                        <div
                          className="border shadow"
                          style={{ height: "350px" }}
                        >
                          <DefaultMap
                            zoom={16}
                            lat={clientData.coordinates.latitude}
                            lng={clientData.coordinates.longitude}
                            //iconUrl={"./../../assets/images/icons/ic_marker_home_156.png"}
                            //iconScale={56}
                            infoHeader={props.t("Address")}
                            infoText={clientData.address}
                          />
                        </div>
                      ) : (
                        <div className="border shadow text-center bg-primary bg-soft p-4">
                          <div className="avatar-md mx-auto">
                            <div className="avatar-title bg-white rounded-circle text-primary h1">
                              <i className="mdi mdi-map-search"></i>
                            </div>
                          </div>
                          <h6 className="text-primary mt-3">
                            {props.t("No location info found")}
                          </h6>
                        </div>
                      )}
                    </Col>
                  </Row>

                  <hr />
                  <CardTitle
                    className="d-flex"
                    style={{ alignItems: "center" }}
                  >
                    <i className="bx bxs-group text-truncate align-middle me-2"></i>
                    {props.t("Contacts")}
                  </CardTitle>
                  {contacts.length > 0 ? (
                    <ul className="list-group list-group-flush">
                      {contacts.map((c, index) => {
                        return (
                          <li key={"" + index} className="list-group-item py-3">
                            <Row>
                              <Col className="col-auto">
                                <span>{index + 1 + ". " + c.name}</span>
                              </Col>
                              <Col>
                                <a
                                  href={"tel:" + c.gsm}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {c.gsm}
                                </a>
                              </Col>
                            </Row>
                          </li>
                        )
                      })}
                    </ul>
                  ) : (
                    <p className="mt-3 mb-4 text-muted">
                      {props.t("Undefined")}
                    </p>
                  )}

                  {isAdmin && (
                    <>
                      <hr />
                      <CardTitle
                        className="mb-4 d-flex"
                        style={{ alignItems: "center" }}
                      >
                        <i className="bx bxs-tag text-truncate align-middle me-2"></i>
                        {props.t("Client tags")}
                      </CardTitle>
                      {clientData?.clientTags?.length > 0 ? (
                        <Space size={[0, 8]} wrap className="mb-3">
                          {clientData.clientTags.map((tag, index) => (
                            <span key={index + "_key"}>
                              <Tag
                                style={{ borderRadius: "5px" }}
                                className="py-1 px-2"
                              >
                                {tag}
                              </Tag>
                            </span>
                          ))}
                        </Space>
                      ) : (
                        <p className="mt-3 mb-4 text-muted">
                          {props.t("Undefined")}
                        </p>
                      )}
                    </>
                  )}

                  <hr />
                  <CardTitle
                    className="mb-4 d-flex"
                    style={{ alignItems: "center" }}
                  >
                    <i className="bx bxs-box text-truncate align-middle me-2"></i>
                    {props.t("Other")}
                  </CardTitle>
                  <Row xs={1} sm={2} className="mt-3">
                    <Col>
                      <div className="text-muted mb-4">
                        <h6>{props.t("Alarms retention period")}:</h6>{" "}
                        {clientData.alarmRetentionPeriod
                          ? clientData.alarmRetentionPeriod +
                            " " +
                            props.t("days")
                          : props.t("Forever")}
                      </div>
                    </Col>
                    <Col>
                      <div className="text-muted mb-4">
                        <h6>{props.t("Health data retention period")}:</h6>{" "}
                        {clientData.healthRetentionPeriod
                          ? clientData.healthRetentionPeriod +
                            " " +
                            props.t("days")
                          : props.t("Forever")}
                      </div>
                    </Col>
                    <Col>
                      <div className="text-muted mb-4">
                        <h6>{props.t("Video logs retention period")}:</h6>{" "}
                        {clientData.videoRetentionPeriod
                          ? clientData.videoRetentionPeriod +
                            " " +
                            props.t("days")
                          : props.t("Forever")}
                      </div>
                    </Col>

                    {clientData?.trackerKey && (
                      <Col>
                        <div className="text-muted mb-4">
                          <h6>{props.t("Tracker data retention period")}:</h6>{" "}
                          {clientData?.tracker?.dataRetention
                            ? clientData.tracker.dataRetention +
                              " " +
                              props.t("days")
                            : props.t("Forever")}
                        </div>
                      </Col>
                    )}

                    {clientData?.gatewayKey && (
                      <>
                        <Col>
                          <div className="text-muted mb-4">
                            <h6>
                              {props.t("Home activity data retention period")}:
                            </h6>{" "}
                            {clientData?.home?.dataRetention
                              ? clientData.home.dataRetention +
                                " " +
                                props.t("days")
                              : props.t("Forever")}
                          </div>
                        </Col>
                        <Col>
                          <div className="text-muted mb-4">
                            <h6>{props.t("Home activity learning period")}:</h6>{" "}
                            {clientData?.home?.learningPeriod
                              ? clientData.home.learningPeriod +
                                " " +
                                props.t("days")
                              : props.t("Undefined")}
                          </div>
                        </Col>

                        <Col>
                          <div className="text-muted mb-4">
                            <h6>{props.t("Rest period starting at")}:</h6>{" "}
                            {clientData?.home?.restStartHourFormatted
                              ? clientData.home.restStartHourFormatted
                              : props.t("Undefined")}
                          </div>
                        </Col>
                      </>
                    )}

                    <Col>
                      <div className="text-muted mb-4">
                        <h6>{props.t("Away from home")}:</h6>{" "}
                        {clientData.away ? props.t("Yes") : props.t("No")}
                      </div>
                    </Col>

                    <Col>
                      <div className="text-muted mb-4">
                        <h6>{props.t("Video call feedback")}:</h6>{" "}
                        {clientData.video?.videoCallFeedback
                          ? props.t("Yes")
                          : props.t("No")}
                      </div>
                    </Col>

                    <Col>
                      <div className="text-muted mb-4">
                        <h6>{props.t("Auto join group call")}:</h6>{" "}
                        {clientData?.autoJoinGroupCall
                          ? props.t("Yes")
                          : props.t("No")}
                      </div>
                    </Col>
                  </Row>
                </>
              )}

              {editMode && (
                <>
                  <Form
                    className="form-horizontal"
                    onSubmit={e => {
                      e.preventDefault()
                      return false
                    }}
                  >
                    <Row xs={1} sm={2} className="mt-3">
                      <Col>
                        <FormGroup>
                          <Label>{props.t("Name")}</Label>
                          <AntdForm.Item
                            validateStatus={
                              validation.errors.name ? "error" : ""
                            }
                            hasFeedback
                            help={validation.errors.name}
                          >
                            <AntdInput
                              allowClear
                              placeholder={props.t("Name")}
                              value={validation.values.name}
                              //onChange={(v) => { validation.setFieldValue("name", v.target.value) }}
                              onChange={v => {
                                const value = v.target.value
                                validation.setFieldValue("name", value)
                                validation.setFieldValue("displayName", value)
                                if (clientData?.video != null) {
                                  validation.setFieldValue(
                                    "videoDisplayName",
                                    value
                                  )
                                }
                              }}
                            />
                          </AntdForm.Item>
                        </FormGroup>
                      </Col>
                      {userRole != "private_user" &&
                        userRole != "organisation_user" && (
                          <Col>
                            <FormGroup>
                              <Label>{props.t("Group")}</Label>
                              <SelectGroup
                                onChange={groupChanged}
                                multiple={false}
                                clearable={false}
                                selection={validation.values.group}
                              />
                            </FormGroup>
                          </Col>
                        )}
                      <Col>
                        <FormGroup>
                          <Label>{props.t("GSM")}</Label>
                          <AntdInput
                            allowClear
                            prefix="+"
                            placeholder={"358401234567"}
                            value={validation.values.gsmNumber}
                            onChange={v => {
                              validation.setFieldValue(
                                "gsmNumber",
                                v.target.value
                              )
                            }}
                          />
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup>
                          <Label>{props.t("SSN")}</Label>
                          <AntdInput
                            allowClear
                            placeholder={props.t("SSN")}
                            value={validation.values.ssnNumber}
                            onChange={v => {
                              validation.setFieldValue(
                                "ssnNumber",
                                v.target.value
                              )
                            }}
                          />
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup>
                          <Label>{props.t("Address")}</Label>
                          <AntdInput
                            allowClear
                            placeholder={props.t("Address")}
                            value={validation.values.address}
                            onChange={v => {
                              validation.setFieldValue(
                                "address",
                                v.target.value
                              )
                            }}
                          />
                        </FormGroup>
                      </Col>
                      <Col>
                        <Row>
                          <Col>
                            <FormGroup>
                              <Label>{props.t("Latitude")}</Label>
                              <AntdInput
                                allowClear
                                placeholder={props.t("E.g. 66.503563")}
                                value={validation.values.latitude}
                                onChange={v => {
                                  validation.setFieldValue(
                                    "latitude",
                                    v.target.value
                                  )
                                }}
                              />
                            </FormGroup>
                          </Col>
                          <Col>
                            <FormGroup>
                              <Label>{props.t("Longitude")}</Label>
                              <AntdInput
                                allowClear
                                placeholder={props.t("E.g. 25.725005")}
                                value={validation.values.longitude}
                                onChange={v => {
                                  validation.setFieldValue(
                                    "longitude",
                                    v.target.value
                                  )
                                }}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col className="col-auto">
                            <Button
                              size="sm"
                              color="primary"
                              disabled={
                                geolocating ||
                                updating ||
                                !validation.values.address
                              }
                              onClick={() => {
                                getCoordinates()
                              }}
                            >
                              {props.t("Get coordinates")}
                            </Button>
                          </Col>
                          <Col className="col-auto">
                            <Button
                              size="sm"
                              color="primary"
                              disabled={geolocating || updating}
                              onClick={() => {
                                setMapModal(true)
                              }}
                            >
                              {props.t("Select from map")}
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                      <Col>
                        <FormGroup>
                          <Label>{props.t("Timezone")}</Label>
                          <Select
                            value={"" + validation.values.timezoneOffset}
                            placeholder={props.t("Choose")}
                            onChange={v => {
                              validation.setFieldValue("timezoneOffset", v)
                            }}
                            options={[
                              { label: "UTC-12", value: "-12" },
                              { label: "UTC-11", value: "-11" },
                              { label: "UTC-10", value: "-10" },
                              { label: "UTC-9", value: "-9" },
                              { label: "UTC-8", value: "-8" },
                              { label: "UTC-7", value: "-7" },
                              { label: "UTC-6", value: "-6" },
                              { label: "UTC-5", value: "-5" },
                              { label: "UTC-4", value: "-4" },
                              { label: "UTC-3", value: "-3" },
                              { label: "UTC-2", value: "-2" },
                              { label: "UTC-1", value: "-1" },
                              { label: "UTC", value: "0" },
                              { label: "UTC+1", value: "1" },
                              { label: "UTC+2", value: "2" },
                              { label: "UTC+3", value: "3" },
                              { label: "UTC+4", value: "4" },
                              { label: "UTC+5", value: "5" },
                              { label: "UTC+6", value: "6" },
                              { label: "UTC+7", value: "7" },
                              { label: "UTC+8", value: "8" },
                              { label: "UTC+9", value: "9" },
                              { label: "UTC+10", value: "10" },
                              { label: "UTC+11", value: "11" },
                              { label: "UTC+12", value: "12" },
                              { label: "UTC+13", value: "13" },
                              { label: "UTC+14", value: "14" },
                            ]}
                            style={{ width: 100 + "%" }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Switch
                            className="me-2"
                            name="timezoneCheckEnabled"
                            checked={validation.values.timezoneCheckEnabled}
                            onChange={v =>
                              validation.setFieldValue(
                                "timezoneCheckEnabled",
                                v
                              )
                            }
                          />
                          <Label>{props.t("Automatic")}</Label>
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup>
                          <Label>{props.t("Language")}</Label>
                          <Select
                            value={validation.values.language}
                            placeholder={props.t("Choose")}
                            onChange={v => {
                              validation.setFieldValue("language", v)
                            }}
                            options={[
                              { label: props.t("English"), value: "en" },
                              { label: props.t("Finnish"), value: "fi" },
                              { label: props.t("Swedish"), value: "sv" },
                            ]}
                            style={{ width: 100 + "%" }}
                          />
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup>
                          <Label>{props.t("App display name")}</Label>
                          <AntdForm.Item
                            validateStatus={
                              validation.errors.displayName ? "error" : ""
                            }
                            hasFeedback
                            help={validation.errors.displayName}
                          >
                            <AntdInput
                              allowClear
                              placeholder={props.t("App display name")}
                              value={validation.values.displayName}
                              onChange={v => {
                                validation.setFieldValue(
                                  "displayName",
                                  v.target.value
                                )
                              }}
                            />
                          </AntdForm.Item>
                        </FormGroup>
                      </Col>

                      {clientData?.videoCallRoomName && (
                        <Col>
                          <FormGroup>
                            <Label>{props.t("Video call display name")}</Label>
                            <AntdForm.Item
                              validateStatus={
                                validation.errors.videoDisplayName
                                  ? "error"
                                  : ""
                              }
                              hasFeedback
                              help={validation.errors.videoDisplayName}
                            >
                              <AntdInput
                                allowClear
                                placeholder={props.t("Video call display name")}
                                value={validation.values.videoDisplayName}
                                onChange={v => {
                                  validation.setFieldValue(
                                    "videoDisplayName",
                                    v.target.value
                                  )
                                }}
                              />
                            </AntdForm.Item>
                          </FormGroup>
                        </Col>
                      )}

                      <Col>
                        <FormGroup>
                          <Label>{props.t("Additional info")}</Label>
                          <TextArea
                            allowClear
                            autoSize={{ minRows: 2, maxRows: 10 }}
                            placeholder={props.t("Additional info")}
                            value={validation.values.appOtherInfo}
                            onChange={v => {
                              validation.setFieldValue(
                                "appOtherInfo",
                                v.target.value
                              )
                            }}
                          />
                        </FormGroup>
                      </Col>
                      {(isAdmin || userRole === "organisation_admin") && (
                        <Col>
                          <FormGroup>
                            <Label>{props.t("Admin notes")}</Label>
                            <TextArea
                              allowClear
                              autoSize={{ minRows: 2, maxRows: 10 }}
                              placeholder={props.t("Admin notes")}
                              value={validation.values.information}
                              onChange={v => {
                                validation.setFieldValue(
                                  "information",
                                  v.target.value
                                )
                              }}
                            />
                          </FormGroup>
                        </Col>
                      )}
                    </Row>

                    <hr />
                    <CardTitle
                      className="d-flex"
                      style={{ alignItems: "center" }}
                    >
                      <i className="bx bxs-group text-truncate align-middle me-2"></i>
                      {props.t("Contacts")}
                    </CardTitle>
                    <Row className="mt-4">
                      <Col className="col-auto">
                        <FormGroup>
                          <Label>{props.t("Name of the contact")}</Label>
                          <AntdInput
                            allowClear
                            style={{ minWidth: "200px" }}
                            placeholder={props.t("Maija Meikäläinen")}
                            value={contactValidation.values.name}
                            onChange={v => {
                              contactValidation.setFieldValue(
                                "name",
                                v.target.value
                              )
                            }}
                          />
                        </FormGroup>
                      </Col>
                      <Col className="col-auto">
                        <FormGroup>
                          <Label>{props.t("Contact person's GSM")}</Label>
                          <AntdForm.Item
                            validateStatus={
                              contactValidation.errors.gsm ? "error" : ""
                            }
                            hasFeedback
                            help={contactValidation.errors.gsm}
                          >
                            <AntdInput
                              allowClear
                              style={{ minWidth: "200px" }}
                              placeholder={props.t("+358401234567")}
                              value={contactValidation.values.gsm}
                              onChange={v => {
                                contactValidation.setFieldValue(
                                  "gsm",
                                  v.target.value
                                )
                              }}
                            />
                          </AntdForm.Item>
                        </FormGroup>
                      </Col>
                      <Col>
                        <Button
                          style={{ marginTop: "28px" }}
                          color="success"
                          className="compact-button"
                          disabled={
                            !contactValidation.values.name ||
                            !contactValidation.values.gsm
                          }
                          onClick={() => {
                            contactValidation.submitForm()
                          }}
                        >
                          {props.t("Add")}
                        </Button>
                      </Col>
                    </Row>

                    {contactsEdit.length > 0 ? (
                      <ul className="list-group list-group-flush">
                        {contactsEdit.map((c, index) => {
                          return (
                            <li
                              key={"" + index}
                              className="list-group-item py-3"
                            >
                              <Row>
                                <Col className="col-auto">
                                  <span>{index + 1 + ". " + c.name}</span>
                                </Col>
                                <Col>
                                  <a
                                    href={"tel:" + c.gsm}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {c.gsm}
                                  </a>
                                </Col>
                                <Col>
                                  <Button
                                    className="float-end"
                                    size="sm"
                                    outline
                                    color="danger"
                                    onClick={() => {
                                      setContactsEdit(prevState =>
                                        prevState.filter(
                                          prevItem =>
                                            prevItem.name !== c.name ||
                                            prevItem.gsm !== c.gsm
                                        )
                                      )
                                    }}
                                  >
                                    {props.t("Delete")}
                                  </Button>
                                </Col>
                              </Row>
                            </li>
                          )
                        })}
                      </ul>
                    ) : (
                      <></>
                    )}

                    {isAdmin && (
                      <>
                        <hr />
                        <CardTitle
                          className="d-flex"
                          style={{ alignItems: "center" }}
                        >
                          <i className="bx bxs-tag text-truncate align-middle me-2"></i>
                          {props.t("Client tags")}
                        </CardTitle>
                        <Row className="mt-4">
                          <Col className="col-auto">
                            <FormGroup>
                              <AntdForm.Item
                                validateStatus={
                                  tagValidation.errors.category ? "error" : ""
                                }
                                hasFeedback
                                help={tagValidation.errors.category}
                              >
                                <AntdInput
                                  allowClear
                                  style={{ minWidth: "240px" }}
                                  placeholder={
                                    props.t("Category") +
                                    " (" +
                                    props.t("Optional") +
                                    ")"
                                  }
                                  value={tagValidation.values.category}
                                  onChange={v => {
                                    tagValidation.setFieldValue(
                                      "category",
                                      v.target.value
                                    )
                                  }}
                                />
                              </AntdForm.Item>
                            </FormGroup>
                          </Col>
                          <Col className="col-auto">
                            <FormGroup>
                              <AntdForm.Item
                                validateStatus={
                                  tagValidation.errors.tag ? "error" : ""
                                }
                                hasFeedback
                                help={tagValidation.errors.tag}
                              >
                                <AntdInput
                                  allowClear
                                  style={{ minWidth: "240px" }}
                                  placeholder={props.t("Tag")}
                                  value={tagValidation.values.tag}
                                  onChange={v => {
                                    tagValidation.setFieldValue(
                                      "tag",
                                      v.target.value
                                    )
                                  }}
                                />
                              </AntdForm.Item>
                            </FormGroup>
                          </Col>
                          <Col>
                            <Button
                              color="success"
                              className="compact-button"
                              disabled={!tagValidation.values.tag}
                              onClick={() => {
                                tagValidation.submitForm()
                              }}
                            >
                              {props.t("Add")}
                            </Button>
                          </Col>
                        </Row>
                        {tagsEdit?.length > 0 && (
                          <Space size={[0, 8]} wrap className="mb-3">
                            {tagsEdit.map((tag, index) => (
                              <span key={index + "_key"}>
                                <Tag
                                  style={{ borderRadius: "5px" }}
                                  key={tag}
                                  className="py-1 px-2"
                                  onClose={() => {
                                    setTagsEdit(prevState =>
                                      prevState.filter(
                                        prevItem => prevItem !== tag
                                      )
                                    )
                                  }}
                                  closable
                                >
                                  {tag}
                                </Tag>
                              </span>
                            ))}
                          </Space>
                        )}
                      </>
                    )}

                    <hr />
                    <CardTitle
                      className="mb-4 d-flex"
                      style={{ alignItems: "center" }}
                    >
                      <i className="bx bxs-box text-truncate align-middle me-2"></i>
                      {props.t("Other")}
                    </CardTitle>
                    {!clientData?.dataRetentionReadonly && (
                      <Alert color="info">
                        {props.t("DataRetentionWarning")}
                      </Alert>
                    )}
                    <Row xs={1} sm={2} className="mt-3">
                      {!clientData?.dataRetentionReadonly && (
                        <Col>
                          <FormGroup>
                            <Label>{props.t("Alarms retention period")}</Label>
                            <InputNumber
                              value={validation.values.storeAlarmsPeriod}
                              suffix={props.t("days")}
                              style={{ width: "100%" }}
                              onChange={v => {
                                validation.setFieldValue("storeAlarmsPeriod", v)
                              }}
                            />
                          </FormGroup>
                        </Col>
                      )}
                      {!clientData?.dataRetentionReadonly && (
                        <Col>
                          <FormGroup>
                            <Label>
                              {props.t("Health data retention period")}
                            </Label>
                            <InputNumber
                              value={validation.values.storeHealthDataPeriod}
                              suffix={props.t("days")}
                              style={{ width: "100%" }}
                              onChange={v => {
                                validation.setFieldValue(
                                  "storeHealthDataPeriod",
                                  v
                                )
                              }}
                            />
                          </FormGroup>
                        </Col>
                      )}
                      {!clientData?.dataRetentionReadonly && (
                        <Col>
                          <FormGroup>
                            <Label>
                              {props.t("Video logs retention period")}
                            </Label>
                            <InputNumber
                              value={validation.values.storeVideoDataPeriod}
                              suffix={props.t("days")}
                              style={{ width: "100%" }}
                              onChange={v => {
                                validation.setFieldValue(
                                  "storeVideoDataPeriod",
                                  v
                                )
                              }}
                            />
                          </FormGroup>
                        </Col>
                      )}

                      {clientData?.trackerKey && (
                        <>
                          {!clientData?.dataRetentionReadonly && (
                            <Col>
                              <FormGroup>
                                <Label>
                                  {props.t("Tracker data retention period")}
                                </Label>
                                <InputNumber
                                  value={
                                    validation.values.storeTrackerDataPeriod
                                  }
                                  suffix={props.t("days")}
                                  style={{ width: "100%" }}
                                  onChange={v => {
                                    validation.setFieldValue(
                                      "storeTrackerDataPeriod",
                                      v
                                    )
                                  }}
                                />
                              </FormGroup>
                            </Col>
                          )}
                        </>
                      )}

                      {clientData?.gatewayKey && (
                        <>
                          {!clientData?.dataRetentionReadonly && (
                            <Col>
                              <FormGroup>
                                <Label>
                                  {props.t(
                                    "Home activity data retention period"
                                  )}
                                </Label>
                                <InputNumber
                                  value={validation.values.storeHomeDataPeriod}
                                  suffix={props.t("days")}
                                  style={{ width: "100%" }}
                                  onChange={v => {
                                    validation.setFieldValue(
                                      "storeHomeDataPeriod",
                                      v
                                    )
                                  }}
                                />
                              </FormGroup>
                            </Col>
                          )}
                          {!clientData?.dataRetentionReadonly && (
                            <Col>
                              <FormGroup>
                                <Label>
                                  {props.t("Home activity learning period")}
                                </Label>
                                <InputNumber
                                  value={validation.values.learningPeriod}
                                  suffix={props.t("days")}
                                  style={{ width: "100%" }}
                                  onChange={v => {
                                    validation.setFieldValue(
                                      "learningPeriod",
                                      v
                                    )
                                  }}
                                />
                              </FormGroup>
                            </Col>
                          )}
                          {!clientData?.dataRetentionReadonly && (
                            <Col>
                              <FormGroup>
                                <Label>
                                  {props.t("Rest period starting at")}
                                </Label>
                                <Select
                                  value={"" + validation.values.restStartHour}
                                  placeholder={props.t("Choose")}
                                  onChange={v => {
                                    validation.setFieldValue("restStartHour", v)
                                  }}
                                  options={[
                                    { label: "00:00", value: "0" },
                                    { label: "01:00", value: "1" },
                                    { label: "02:00", value: "2" },
                                    { label: "03:00", value: "3" },
                                    { label: "04:00", value: "4" },
                                    { label: "05:00", value: "5" },
                                    { label: "06:00", value: "6" },
                                    { label: "07:00", value: "7" },
                                    { label: "08:00", value: "8" },
                                    { label: "09:00", value: "9" },
                                    { label: "10:00", value: "10" },
                                    { label: "11:00", value: "11" },
                                    { label: "12:00", value: "12" },
                                    { label: "13:00", value: "13" },
                                    { label: "14:00", value: "14" },
                                    { label: "15:00", value: "15" },
                                    { label: "16:00", value: "16" },
                                    { label: "17:00", value: "17" },
                                    { label: "18:00", value: "18" },
                                    { label: "19:00", value: "19" },
                                    { label: "20:00", value: "20" },
                                    { label: "21:00", value: "21" },
                                    { label: "22:00", value: "22" },
                                    { label: "23:00", value: "23" },
                                    { label: "24:00", value: "24" },
                                  ]}
                                  style={{ width: 100 + "%" }}
                                />
                              </FormGroup>
                            </Col>
                          )}
                        </>
                      )}

                      <Col>
                        <Label>{props.t("Away from home")}</Label>
                        <FormGroup className="mt-2">
                          <Switch
                            className=""
                            name="away"
                            checked={validation.values.away}
                            onChange={v => validation.setFieldValue("away", v)}
                          />
                        </FormGroup>
                      </Col>

                      {clientData?.videoCallRoomName &&
                        (isAdmin || userRole === "organisation_admin") && (
                          <>
                            <Col>
                              <Label>{props.t("Video call feedback")}</Label>
                              <FormGroup className="mt-2">
                                <Switch
                                  className=""
                                  name="videoCallFeedback"
                                  checked={validation.values.videoCallFeedback}
                                  onChange={v =>
                                    validation.setFieldValue(
                                      "videoCallFeedback",
                                      v
                                    )
                                  }
                                />
                              </FormGroup>
                            </Col>
                          </>
                        )}
                      {(isAdmin || userRole === "organisation_admin") && (
                        <Col>
                          <Label>{props.t("Auto join group call")}</Label>
                          <FormGroup className="mt-2">
                            <Switch
                              className=""
                              name="autoJoinGroupCall"
                              checked={validation.values.autoJoinGroupCall}
                              onChange={v =>
                                validation.setFieldValue("autoJoinGroupCall", v)
                              }
                            />
                          </FormGroup>
                        </Col>
                      )}
                    </Row>
                  </Form>
                </>
              )}
            </CardBody>
          </Card>
        </>
      )}

      <Modal
        isOpen={mapModal}
        toggle={() => {
          setMapModal(!mapModal)
        }}
        centered
      >
        <ModalHeader>{props.t("Location")}</ModalHeader>
        <ModalBody>
          <Alert color="info">
            {props.t(
              "Change the location by dragging the marker or clicking a spot on the map"
            )}
          </Alert>
          <div style={{ height: "360px" }}>
            <DefaultMap
              zoom={10}
              lat={validation.values.latitude}
              lng={validation.values.longitude}
              markerEdit={true}
              markerChanged={mapLocationChanged}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            outline
            onClick={() => {
              setMapModal(false)
            }}
          >
            {props.t("Close")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

ClientInfo.propTypes = {
  t: PropTypes.any,
  suvantoKey: PropTypes.any,
  setClientName: PropTypes.any,
}

export default withTranslation()(ClientInfo)
